import React, { useRef, useState } from 'react';
import { sizes } from '@sharefiledev/antd-config';
import { InputRef, Modal, Select, theme, Typography } from 'antd';
import isEqual from 'lodash/isEqual';
import { StyledInput } from '../../../../common/custom-input/CustomInput.styled';
import {
	ALLOWED_CURRENCIES,
	ALLOWED_DATE_FORMAT,
	DISALLOWED_NAME_PREFIXS,
} from '../../../../constants/constants';
import { CurrencyEnum, fieldProps, FieldsEnum } from '../../../../constants/fields';
import { t } from '../../../../utils';
import FieldsConfiguration from '../../../Fields/Configuration';
import { FieldsArray } from '../../../Layouts';
import { useTableContext } from '../../context/TableContext';
import {
	ColumnDefinition,
	IsManageColumnValid,
	ManageColumnModalProps,
} from '../../DataTable';
import { TableAction } from '..';
import { CurrencyBuilder } from './CurrencyBuilder';
import { DateBuilder } from './fieldBuilder/DateBuilder';
import {
	ContainerFlex,
	IconStyle,
	InputWrapperFlex,
	LabelStyle,
	StyledChevronDown,
} from './ManageColumnModal.styled';
import { SelectFieldBuilder } from './SelectFieldBuilder';

const defaultIsValid = { label: false, fieldBuilder: false };
const fieldBuilderNeeded: FieldsArray = [
	FieldsEnum.Select,
	FieldsEnum.CurrencyField,
	FieldsEnum.DateOverdueField,
];
export const ManageColumnModal = ({
	editColumnSchema: ColumnSchema,
	setManageColumnModal,
}: ManageColumnModalProps) => {
	const [isValid, setIsValid] = useState<IsManageColumnValid>(defaultIsValid);
	const inputRef = useRef<InputRef>(null);
	const { allowedCurrencies, allowedDateFormat, allowedColumnFields, onColumnAction } =
		useTableContext();
	const { token } = theme.useToken();
	const fields: { label: React.ReactElement; value: FieldsEnum }[] = [];
	const isStatusField = ColumnSchema?.attributes?.subType === FieldsEnum.Status;
	const editColumnSchema = ColumnSchema
		? {
				...ColumnSchema,
				component: isStatusField ? FieldsEnum.Status : ColumnSchema?.component,
		  }
		: ColumnSchema;

	const defaultAddColumnFormValues: ColumnDefinition = {
		label: '',
		name: '',
		component: allowedColumnFields.length
			? allowedColumnFields[0]
			: FieldsEnum.TextInputField,
		validations: allowedColumnFields.length
			? fieldProps[allowedColumnFields[0]]?.validations
			: undefined,
		attributes: allowedColumnFields.length
			? fieldProps[allowedColumnFields[0]]?.attributes
			: undefined,
	};
	const [addColumnFormValues, setAddColumnFormValues] = useState<ColumnDefinition>(
		editColumnSchema ?? defaultAddColumnFormValues
	);
	const isLabelValid =
		!!addColumnFormValues.label &&
		!DISALLOWED_NAME_PREFIXS.some(prefix => prefix === addColumnFormValues.label?.[0]);
	const [confirmLoading, setConfirmLoading] = useState(false);

	const getActionText = (isEdit: boolean) => ({
		title: t(`dynamic-components:manageField.${isEdit ? 'edit' : 'add'}`),
		okText: t(`dynamic-components:manageField.${isEdit ? 'saveButton' : 'createButton'}`),
	});

	const { title, okText } = getActionText(!!editColumnSchema);

	const onSaveButtonClick = async () => {
		setConfirmLoading(true);

		try {
			let modifiedColumnSchema: ColumnDefinition = addColumnFormValues;
			const component =
				modifiedColumnSchema.component === FieldsEnum.Status
					? FieldsEnum.Select
					: modifiedColumnSchema.component;
			if (!editColumnSchema) {
				const date = new Date().getTime();
				const newName = `${addColumnFormValues.name}-${date}`;
				modifiedColumnSchema = { ...modifiedColumnSchema, name: newName };
			}

			const actionResult = await onColumnAction({
				action: editColumnSchema ? TableAction.EDIT_COLUMN : TableAction.ADD_COLUMN,
				value: { ...modifiedColumnSchema, component },
			});

			// Check the result from the action
			if (actionResult?.success) {
				onCancelButtonClick(); // Reset the modal
			}
		} catch (error) {
			console.error('An error occurred while saving the column:', error);
		} finally {
			setConfirmLoading(false); // Stop the loader after everything is done
		}
	};

	const onCancelButtonClick = () => {
		setManageColumnModal(false);
		setAddColumnFormValues(defaultAddColumnFormValues);
		setIsValid(defaultIsValid);
	};
	allowedColumnFields.forEach((fieldKey: FieldsEnum) => {
		const fieldConfig = FieldsConfiguration[fieldKey as keyof typeof FieldsConfiguration];
		if (fieldConfig) {
			const { Icon, label } = fieldConfig;
			fields.push({
				label: (
					<LabelStyle tabIndex={0} id={fieldKey}>
						<IconStyle>
							<Icon />
						</IconStyle>
						{label}
					</LabelStyle>
				),
				value: fieldKey,
			});
		}
	});

	const onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const labelValue: string = e.target.value;
		const fieldName: string = labelValue.toLowerCase().replaceAll(' ', '-');

		// Update state based on editHeaderColumnName flag
		setAddColumnFormValues({
			...addColumnFormValues,
			label: labelValue,
			...(editColumnSchema ? {} : { name: fieldName }), // Only update name if not in edit mode
		});

		setIsValid({
			...isValid,
			label: !!labelValue,
		});
	};

	const onFieldTypeChange = (fieldName: FieldsEnum) => {
		setAddColumnFormValues({
			...addColumnFormValues,
			component: fieldName,
			validations: fieldProps[fieldName]?.validations,
			attributes: fieldProps[fieldName]?.attributes,
		});
	};

	const renderFieldBuilder = (fieldType: FieldsEnum) => {
		switch (fieldType) {
			case FieldsEnum.Select:
			case FieldsEnum.Status: {
				return (
					<SelectFieldBuilder
						editColumnSchema={editColumnSchema}
						isValid={isValid}
						setIsValid={setIsValid}
						addColumnFormValues={addColumnFormValues}
						setAddColumnFormValues={setAddColumnFormValues}
					/>
				);
			}
			case FieldsEnum.CurrencyField: {
				return (
					<CurrencyBuilder
						isEdit={!!editColumnSchema}
						value={addColumnFormValues.attributes.currency}
						onChange={onCurrencyChange}
						allowedCurrencies={allowedCurrencies ?? ALLOWED_CURRENCIES}
					/>
				);
			}
			case FieldsEnum.DateOverdueField: {
				return (
					<DateBuilder
						isEdit={!!editColumnSchema}
						value={addColumnFormValues.attributes.format}
						allowedDateFormat={allowedDateFormat ?? ALLOWED_DATE_FORMAT}
						onChange={onDateFormatChange}
					/>
				);
			}
			default: {
				return null;
			}
		}
	};

	const isFormValid = () => {
		const { fieldBuilder, label } = isValid;
		const isFieldBuilderNeeded = fieldBuilderNeeded.includes(
			addColumnFormValues.component
		);

		const formValidForNonEdit =
			label && isLabelValid && (isFieldBuilderNeeded ? fieldBuilder : true);

		// For edit mode
		if (!editColumnSchema) {
			return formValidForNonEdit;
		}

		const isLabelModified = editColumnSchema.label !== addColumnFormValues.label;
		const isFieldBuilderModified =
			editColumnSchema.component !== addColumnFormValues.component ||
			!isEqual(editColumnSchema.attributes, addColumnFormValues.attributes) ||
			!isEqual(editColumnSchema.validations, addColumnFormValues.validations);

		const isFieldBuilderChangedAndValid =
			fieldBuilder && !isLabelModified && isFieldBuilderModified;

		const isLabelChangedAndValid = label && !isFieldBuilderModified && isLabelModified;

		const formValidForEdit =
			label &&
			isLabelModified &&
			(isFieldBuilderNeeded ? fieldBuilder && isFieldBuilderModified : true);

		return (
			isLabelValid &&
			(formValidForEdit || isLabelChangedAndValid || isFieldBuilderChangedAndValid)
		);
	};

	const onCurrencyChange = (currency: CurrencyEnum) => {
		setIsValid({ ...isValid, fieldBuilder: true });
		setAddColumnFormValues({
			...addColumnFormValues,
			attributes: {
				...addColumnFormValues.attributes,
				currency,
			},
		});
	};

	const onDateFormatChange = (value: string) => {
		setIsValid({ ...isValid, fieldBuilder: true });
		setAddColumnFormValues({
			...addColumnFormValues,
			attributes: {
				...addColumnFormValues.attributes,
				format: value,
			},
		});
	};

	return (
		<Modal
			title={title}
			afterOpenChange={open => open && inputRef.current?.focus()}
			centered
			open
			okText={okText}
			cancelText={t('dynamic-components:addTab.cancel')}
			onOk={onSaveButtonClick}
			onCancel={onCancelButtonClick}
			confirmLoading={confirmLoading}
			maskClosable={false}
			okButtonProps={{
				id: editColumnSchema
					? `edit-field-${addColumnFormValues.component}`
					: `create-field-${addColumnFormValues.component}`,
				disabled: !isFormValid(),
				size: 'small',
				'data-testid': 'manage-column-submit',
			}}
			cancelButtonProps={{ size: 'small' }}
		>
			<ContainerFlex data-testid="manage-column-modal">
				<InputWrapperFlex>
					<Typography.Text id="add-field-title-label">
						{t('dynamic-components:manageField.textField.label')}
					</Typography.Text>
					<StyledInput
						aria-labelledby="add-field-title-label"
						value={addColumnFormValues.label}
						data-testid="add-column-title"
						id="title"
						status={!isLabelValid ? 'error' : ''}
						placeholder={t('dynamic-components:manageField.textField.placeholder')}
						onChange={onLabelChange}
						ref={inputRef}
					/>
					{isValid.label && !isLabelValid && (
						<Typography.Text style={{ color: token.colorError }} id="error-message-label">
							{t('dynamic-components:manageField.textField.errorMessage')}
						</Typography.Text>
					)}
				</InputWrapperFlex>
				<InputWrapperFlex>
					<Typography.Text id="field-picker-label">
						{t('dynamic-components:manageField.fieldPicker.label')}
					</Typography.Text>
					<Select
						aria-labelledby={`field-picker-label ${addColumnFormValues?.component}`}
						suffixIcon={<StyledChevronDown size={parseInt(sizes.base, 10)} />}
						value={addColumnFormValues.component}
						options={fields} // this we need to get from props
						onChange={onFieldTypeChange}
						data-testid="add-column-select"
						disabled={!!editColumnSchema}
					/>
				</InputWrapperFlex>
			</ContainerFlex>

			{addColumnFormValues.component && renderFieldBuilder(addColumnFormValues.component)}
		</Modal>
	);
};
